import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import useResizeObserver from '@/hooks/use-resize-observer';
import { parseSearchParamsToSearchQuery } from '@/utils/parseSearchParamsToSearchQuery';

const route = getRouteApi('/_authenticated/_sidebar-layout/projects/');

import { EstateGrid } from '../estates/components/estate-grid';
import { EstatesList } from '../estates/components/estate-list';
import { getProjectsQueryOptions } from './api/projects';
import { getProjectListFilters, hasUnsupportedProjectFilters } from './utils/project-filters';

export const ProjectsSection = () => {
  const { view, filters, sort } = route.useSearch();
  const navigate = route.useNavigate();
  const isDesktopView = useResizeObserver(768);

  const parsedFilters = parseSearchParamsToSearchQuery(filters);
  const projectFilters = useMemo(() => getProjectListFilters(parsedFilters), [parsedFilters]);
  const hasProjectBlockingFilters = useMemo(
    () => hasUnsupportedProjectFilters(parsedFilters),
    [parsedFilters]
  );

  const queryEstatesOptions = useMemo(
    () => ({
      query: {
        ...(Object.keys(projectFilters).length ? { filters: projectFilters } : {}),
        ...(sort &&
          (sort.by || sort.order) && {
            sort: {
              ...(sort.by && { by: sort.by }),
              ...(sort.order && { order: sort.order }),
            },
          }),
        pageSize: view === 'map' ? 999 : 20,
      },
    }),
    [view, projectFilters, sort]
  );

  const { data: projectsData, isPending: projectsPending } = useQuery({
    ...getProjectsQueryOptions(queryEstatesOptions),
    enabled: !hasProjectBlockingFilters,
  });
  const projects = hasProjectBlockingFilters ? [] : (projectsData ?? []);

  useEffect(() => {
    if (!isDesktopView && view !== 'grid') {
      navigate({
        search: (prev: Record<string, string>) => ({ ...prev, view: 'grid' }),
      });
    }
  }, [isDesktopView, navigate, view]);

  if (view === 'grid') {
    return (
      <EstateGrid
        data={projects}
        filters={projectFilters}
        infiniteScroll={!hasProjectBlockingFilters}
        isPending={!hasProjectBlockingFilters && projectsPending}
        sort={sort}
        type="project"
      />
    );
  }

  return (
    <EstatesList
      data={projects}
      filters={projectFilters}
      infiniteScroll={!hasProjectBlockingFilters}
      isPending={!hasProjectBlockingFilters && projectsPending}
      route={route}
      type="project"
    />
  );
};
